import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import {
  PlusOutlined,
  DollarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { message, Button, Empty, theme, Col, Row } from "antd";
import { axiosInstance } from "../../../authProvider";
import { CreditBuyModal } from "../../../components/credit-buy-modal";
import { GlassCard, ActionButton } from "../../../shared/components";
import { useNavigate } from "react-router-dom";
import { useApiUrl } from "@refinedev/core";
import type { DashboardInvoice } from "../types";

export interface CreditsAndInvoiceCardProps {
  billingMode?: string;
  accountCredit: number;
  latestInvoice: DashboardInvoice | undefined;
  pricePerCredit: number;
  accountId: number;
  isCreditModalOpen: boolean;
  onCreditModalOpen: (open: boolean) => void;
  downloadingInvoiceId: number | null;
  onDownloadingInvoiceId: (id: number | null) => void;
}

export function CreditsAndInvoiceCard({
  billingMode,
  accountCredit,
  latestInvoice,
  pricePerCredit,
  accountId,
  isCreditModalOpen,
  onCreditModalOpen,
  downloadingInvoiceId,
  onDownloadingInvoiceId,
}: CreditsAndInvoiceCardProps) {
  const _t = useTranslate();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const { token } = theme.useToken();
  interface InvoiceDownloadData {
    qontoInvoiceUrl?: string | null;
  }
  const downloadLocalInvoicePdf = async (invoice: DashboardInvoice): Promise<void> => {
    if (!invoice.downloadLink) {
      throw new Error("missing_download_link");
    }
    const pdfUrl = invoice.downloadLink.startsWith("http")
      ? invoice.downloadLink
      : `${apiUrl}${invoice.downloadLink}`;
    const response = await axiosInstance.get(pdfUrl, {
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "application/pdf" });
    const objectUrl = window.URL.createObjectURL(blob);
    const linkElement = document.createElement("a");
    linkElement.href = objectUrl;
    linkElement.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadInvoice = async (invoice: DashboardInvoice) => {
    if (!invoice.downloadLink) return;
    onDownloadingInvoiceId(invoice.id);
    try {
      const invoiceResponse = await axiosInstance.get<InvoiceDownloadData>(
        `${apiUrl}/invoices/${invoice.id}`,
      );
      const qontoInvoiceUrl = invoiceResponse.data?.qontoInvoiceUrl;
      if (qontoInvoiceUrl) {
        const openedWindow = window.open(
          qontoInvoiceUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (!openedWindow) {
          throw new Error("popup_blocked");
        }
        return;
      }
      await downloadLocalInvoicePdf(invoice);
    } catch (error) {
      console.error("Download error:", error);
      message.error(
        _t(
          "invoices.errors.downloadFailed",
          "Échec du téléchargement de la facture",
        ),
      );
    } finally {
      onDownloadingInvoiceId(null);
    }
  };


  return (
    <>
      <GlassCard variant="indigo">
        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          {billingMode === "prepaid" && (
            <Col sm={24} lg={10}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center border border-amber-400/20 bg-gradient-to-br from-amber-400/20 to-amber-400/5">
                  <DollarOutlined className="text-[26px]" style={{ color: token.colorPrimary }} />
                </div>
                <div>
                  <p className="m-0 text-sm font-semibold" style={{ color: token.colorPrimaryText }}>
                    {_t("accounts.fields.credit", "Crédits disponibles")}
                  </p>
                  <p className="m-0 text-xs text-black/40 dark:text-white/40">
                    {_t("dashboard.credits.forRequests", "Pour vos demandes")}
                  </p>
                </div>
              </div>
              <div className="mb-4">
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-5xl font-bold leading-none tracking-tight" style={{ color: token.colorPrimary }}>
                    {accountCredit}
                  </span>
                </div>
              </div>
              <Button
                onClick={() => onCreditModalOpen(true)}
                icon={<PlusOutlined />}
                className="w-full justify-center p-5"
              >
                {_t("credits.titles.buy", "Acheter des crédits")}
              </Button>
            </Col>
          )}

          {billingMode === "prepaid" && latestInvoice && (
            <div className="w-px h-full bg-gradient-to-b from-transparent via-black/10 to-transparent dark:via-white/10" />
          )}

          {latestInvoice ? (
            <Col sm={24} lg={13}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center border border-indigo-400/20 bg-gradient-to-br from-indigo-400/20 to-indigo-400/5">
                  <FileTextOutlined className="text-[22px]" style={{ color: token.colorInfo }} />
                </div>
                <div>
                  <p className="m-0 text-sm font-semibold" style={{ color: token.colorText }}>
                    {_t("invoices.titles.latestInvoice", "Dernière facture")}
                  </p>
                  <p className="m-0 text-xs text-black/40 dark:text-white/40">
                    <DateField
                      value={latestInvoice.createdAt}
                      format="DD MMM YYYY"
                    />
                  </p>
                </div>
              </div>
              <div className="rounded-xl p-4 mb-4 bg-black/[0.02] dark:bg-white/[0.02]">
                {latestInvoice.amount !== undefined && (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[13px] text-black/50 dark:text-white/50">
                        {_t("invoices.fields.amount", "Montant")}
                      </span>
                      <span className="text-lg font-semibold" style={{ color: token.colorText }}>
                        {latestInvoice.amount.toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[13px] text-black/50 dark:text-white/50">
                        {_t("credits.credits", "Crédits")}
                      </span>
                      <span className="text-sm font-medium" style={{ color: token.colorText }}>
                        {Math.floor(latestInvoice.amount / pricePerCredit)}
                      </span>
                    </div>
                  </>
                )}
                {latestInvoice.creditsPurchased !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-[13px] text-black/50 dark:text-white/50">
                      {_t(
                        "dashboard.invoice.creditsPurchased",
                        "Crédits achetés",
                      )}
                    </span>
                    <span className="text-sm font-medium" style={{ color: token.colorSuccess }}>
                      +{latestInvoice.creditsPurchased}{" "}
                      {_t("dashboard.invoice.credits", "crédits")}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 max-h-12">
                {latestInvoice.downloadLink && (
                  <ActionButton
                    variant="secondary"
                    onClick={() => handleDownloadInvoice(latestInvoice)}
                    icon={<DownloadOutlined />}
                    className={`flex-1 justify-center ${downloadingInvoiceId === latestInvoice.id ? "opacity-60 pointer-events-none" : "opacity-100 pointer-events-auto"}`}
                  >
                    {downloadingInvoiceId === latestInvoice.id
                      ? _t("invoices.downloading", "Téléchargement...")
                      : _t("invoices.titles.download", "Télécharger")}
                  </ActionButton>
                )}
                <ActionButton
                  variant="secondary"
                  onClick={() =>
                    navigate(`/accounts/${accountId}/invoices/list`)
                  }
                  icon={<EyeOutlined />}
                  className="flex-1 justify-center"
                >
                  {_t("dashboard.invoice.viewAll", "Voir toutes les factures")}
                </ActionButton>
              </div>
            </Col>
          ) : (
            <div className="flex-1 pt-4">
              <Empty
                description={_t(
                  "invoices.titles.noInvoice",
                  "Aucune facture trouvée",
                )}
              />
            </div>
          )}
        </Row>
      </GlassCard>

      <CreditBuyModal
        isOpen={isCreditModalOpen}
        onCancel={() => onCreditModalOpen(false)}
        accountCredit={accountCredit || 0}
      />
    </>
  );
}
